/**
 * PermissionGuard — capability-based access gate, driven by the user's resolved
 * caps from GET /me/permissions (the same matrix the backend enforces on). This is
 * the frontend twin of the backend `requirePermission(action, subject, scope?)`.
 *
 * NOTE: this is a UX affordance, NOT a security boundary. It only decides what to
 * show/where to send the user. Real enforcement is the backend `requirePermission`
 * on every route — a client-side check can always be bypassed.
 *
 * Route usage (render the page, or redirect if not permitted):
 *   <Route path="dashboard" element={
 *     <PermissionGuard action="view" subject="DASHBOARD" redirect="/">
 *       <Dashboard />
 *     </PermissionGuard>
 *   } />
 *
 * GFG-only surface (require the widest scope):
 *   <PermissionGuard action="view" subject="USER" scope="all" redirect="/dashboard">
 *     <UsersAndOrgs />
 *   </PermissionGuard>
 *
 * Client-only surface (GFG staff must impersonate to reach it):
 *   <PermissionGuard action="create" subject="CAMPAIGN" clientOnly redirect="/dashboard">
 *     <CampaignBuilder />
 *   </PermissionGuard>
 */
import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { usePermissions } from "@/hooks/usePermissions";
import type { AccessScope } from "@/interfaces/permissions";
import { passesPermissionGate } from "@/utils/permission-helper";

interface PermissionGuardProps {
  /** Required action (e.g. "view"). */
  action: string;
  /** Required subject (e.g. "DASHBOARD"). */
  subject: string;
  /** Optional minimum scope — e.g. "all" gates a GFG-only surface, "org" a client-admin one. */
  scope?: AccessScope;
  /** Rendered when permitted. */
  children: ReactNode;
  /**
   * Additionally require that this is NOT a GFG staff session. The route twin of
   * `PermissionGate`'s prop of the same name — see it for why the capability check alone cannot
   * express this, and why impersonation satisfies it without any extra state.
   *
   * Hiding the entry point is not enough on its own: the route stays reachable by typing its URL,
   * which is the path the ticket says should not exist.
   */
  clientOnly?: boolean;
  /** If set and not permitted, redirect here (route-level protection). */
  redirect?: string;
  /** Rendered when not permitted and no `redirect` is set. Defaults to null (hidden). */
  fallback?: ReactNode;
}

export function PermissionGuard({
  action,
  subject,
  scope,
  clientOnly = false,
  children,
  redirect,
  fallback = null,
}: PermissionGuardProps) {
  const { can, isGfg, isLoaded } = usePermissions();

  // Wait for caps before deciding, so we don't redirect on a not-yet-loaded state.
  if (!isLoaded) return null;

  if (passesPermissionGate({ permitted: can(action, subject, scope), clientOnly, isGfg }))
    return <>{children}</>;
  if (redirect) return <Navigate to={redirect} replace />;
  return <>{fallback}</>;
}
